import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { AMAZON_RECONCILIATION_EXCEPTIONS } from '../../consts/reports';
import { ROUTES } from '../../consts/general';

const money = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
});

const AmazonReconciliationExceptionsReport = ({ marginFromSidebar }) => {
  const [report, setReport] = useState({ data: [], summary: {}, totalPages: 1 });
  const [month, setMonth] = useState('');
  const [status, setStatus] = useState('');
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: 50 };
      if (month) params.month = month;
      if (status) params.status = status;
      const { data } = await axios.get('/api/v1/reports/amazon-reconciliation-exceptions', { params });
      setReport(data);
    } catch (error) {
      setMessage(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  }, [month, page, status]);

  useEffect(() => { load(); }, [load]);

  const upload = async (event) => {
    event.preventDefault();
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post('/api/v1/amazon/reconciliation/date-proposals', form);
      setMessage(`Uploaded ${data.source_file_name}: ${data.counts.matched || 0} matched, ${data.counts.ambiguous || 0} ambiguous, ${data.counts.orphan_csv || 0} orphan CSV.`);
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || error.message);
    }
  };

  const apply = async (ids, all = false) => {
    const scope = all
      ? (month ? { month } : { all: true })
      : { proposal_ids: report.data.filter((row) => row.status === 'matched').map((row) => row.proposal_id) };
    const body = all
      ? { ...scope, status: 'matched', preview: true }
      : { proposal_ids: ids, preview: true };
    try {
      const preview = await axios.post('/api/v1/amazon/reconciliation/date-proposals/apply', body);
      const count = preview.data.matched;
      const total = preview.data.total;
      if (!window.confirm(`Apply ${count} matched row(s), dating ${money(total)}?`)) return;
      await axios.post('/api/v1/amazon/reconciliation/date-proposals/apply', all
        ? { ...scope, status: 'matched' }
        : { proposal_ids: ids });
      setMessage('Applied approved date proposals.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || error.message);
    }
  };

  const reject = async (ids) => {
    if (!window.confirm(`Reject ${ids.length} proposal(s)?`)) return;
    await axios.post('/api/v1/amazon/reconciliation/date-proposals/reject', { proposal_ids: ids });
    await load();
  };

  const summary = report.summary || {};
  const tile = (label, key) => (
    <div className="rounded border bg-white p-3 shadow-sm" key={key}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold">{summary[key]?.rows || 0}</div>
      <div className="text-xs text-gray-600">{money(summary[key]?.total)}</div>
    </div>
  );

  return (
    <ReportTemplate
      title="Amazon Reconciliation Exceptions"
      reportId={AMAZON_RECONCILIATION_EXCEPTIONS}
      marginFromSidebar={marginFromSidebar}
      renderChildrenOnly
    >
      <div className="space-y-5">
        {message && <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm">{message}</div>}
        <div className="grid gap-3 md:grid-cols-6">
          {tile('Dateless rows', 'dateless')}
          {tile('Matched', 'matched')}
          {tile('Ambiguous', 'ambiguous')}
          {tile('No match', 'no_match')}
          {tile('Covered uploads', 'covered_uploads')}
          {tile('Orphan CSV', 'orphan_csv')}
        </div>
        <div className="flex flex-wrap items-end gap-3 rounded border bg-white p-4">
          <label className="text-sm">Month<input type="month" value={month} onChange={(event) => { setPage(1); setMonth(event.target.value); }} className="mt-1 block rounded border px-2 py-1" /></label>
          <label className="text-sm">Status<select value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }} className="mt-1 block rounded border px-2 py-1"><option value="">All</option><option value="matched">Matched</option><option value="ambiguous">Ambiguous</option><option value="no_match">No match</option><option value="orphan_csv">Orphan CSV</option></select></label>
          <form onSubmit={upload} className="flex items-end gap-2"><input type="file" accept=".csv,text/csv" onChange={(event) => setFile(event.target.files?.[0] || null)} /><button type="submit" disabled={!file} className="rounded bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50">Upload for review</button></form>
          <button type="button" onClick={() => apply([], true)} className="rounded bg-green-700 px-3 py-2 text-sm text-white">Apply all matched</button>
          <a className="rounded border px-3 py-2 text-sm" href={`${ROUTES.API_AMAZON_RECONCILIATION_EXCEPTIONS}/export?${new URLSearchParams({ ...(month ? { month } : {}), ...(status ? { status } : {}) }).toString()}`}>Export CSV</a>
        </div>
        <div className="overflow-auto rounded border bg-white">
          {loading ? <div className="p-6 text-sm">Loading…</div> : (
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-100"><tr>{['Month', 'Type', 'Description', 'Order/SKU', 'Amount', 'Reason', 'Proposed date', 'Rule', 'Candidates', 'Group counts', 'Coverage', 'Status', 'Actions'].map((label) => <th key={label} className="whitespace-nowrap px-3 py-2">{label}</th>)}</tr></thead>
              <tbody>{report.data.map((row) => (
                <tr key={row.proposal_id || `detail-${row.detail_id}`} className="border-t">
                  <td className="px-3 py-2">{row.posted_month || '—'}</td><td className="px-3 py-2">{row.type || '—'}</td><td className="max-w-xs px-3 py-2">{row.description || '—'}</td><td className="px-3 py-2">{row.order_id || row.sku || '—'}</td><td className="px-3 py-2">{money(row.total)}</td><td className="px-3 py-2">{row.reconciliation_exception_reason || '—'}</td><td className="px-3 py-2">{row.proposed_posted_basis_date || '—'}</td><td className="px-3 py-2">{row.match_rule || '—'}</td><td className="px-3 py-2">{row.candidate_dates?.join(', ') || '—'}</td><td className="px-3 py-2">{row.candidate_count || 0} / {row.detail_count || 0}</td><td className="px-3 py-2">{row.coverage_count || 0}</td><td className="px-3 py-2">{row.status}</td>
                  <td className="whitespace-nowrap px-3 py-2">{row.status === 'matched' && <button type="button" className="mr-2 text-green-700 underline" onClick={() => apply([row.proposal_id])}>Apply</button>}{row.proposal_id && <button type="button" className="text-red-700 underline" onClick={() => reject([row.proposal_id])}>Reject</button>}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
        <div className="flex justify-between text-sm"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button><span>Page {page} of {report.totalPages || 1}</span><button type="button" disabled={page >= (report.totalPages || 1)} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button></div>
      </div>
    </ReportTemplate>
  );
};

export default AmazonReconciliationExceptionsReport;
